import React, { Fragment, Suspense } from 'react';
import { type ValueType } from '@atlaskit/select';
import {
	type OptionType,
	type FilterComponentProps,
	type FilterComponentState,
} from '../filter-show-more';

type InputActionTypes = 'set-value' | 'input-change' | 'input-blur' | 'menu-close';

function isOptionType<T>(o: ValueType<OptionType<T>>): o is OptionType<T> {
	return (o as OptionType<T>).value !== undefined;
}

const AsyncSelect = React.lazy(() =>
	import(
		/* webpackPrefetch: true */
		/* webpackChunkName: "@atlaskit-internal_@atlassian/search-dialog/show-more/select" */ '@atlaskit/select/AsyncSelect'
	).then((module) => {
		return {
			default: module.default,
		};
	}),
);

export class AsyncSelectFilterComponent<T = 'string'> extends React.Component<
	FilterComponentProps<T>,
	FilterComponentState
> {
	constructor(props: FilterComponentProps<T>) {
		super(props);
		this.state = {
			menuIsOpen: false,
			inputValue: '',
		};
	}

	onChange = (
		value: unknown, // Async load types don't accept generics hence this breaks.
		{ action }: { action: string },
	) => {
		if (
			isOptionType(value as ValueType<OptionType<T>>) &&
			(action === 'select-option' || action === 'create-option')
		) {
			this.props.onMenuClosed();
			this.props.refocusEditLink();
			this.props.onConfirm(value as OptionType<T>);
		}
	};

	private handleFocus = () => {
		this.setState({ menuIsOpen: true });
	};

	private handleInputChange = (query: string, { action }: { action: InputActionTypes }) => {
		const { inputValue } = this.state;
		if (action === 'input-change' || action === 'set-value') {
			this.setState({ inputValue: query });
			return query;
		}
		if (action === 'menu-close') {
			// want to keep menu open if user clicks on input box
			this.setState({
				menuIsOpen: true,
				inputValue,
			});
			// need to return inputValue to override react-select resetting
			// to empty string and clearing options if user clicks on input box
			return inputValue;
		}
	};

	render() {
		const { menuIsOpen, inputValue } = this.state;

		return (
			<Suspense fallback={<Fragment></Fragment>}>
				<AsyncSelect
					defaultOptions={this.props.defaultOptions}
					loadOptions={this.props.loadOptions}
					autoFocus
					openMenuOnFocus
					ariaLiveMessages={
						this.props.onFocusAriaMessage ? { onFocus: this.props.onFocusAriaMessage } : undefined
					}
					inputValue={inputValue}
					menuIsOpen={menuIsOpen}
					placeholder={this.props.placeholderText}
					loadingMessage={this.props.loadingMessage}
					noOptionsMessage={this.props.noOptionsMessage}
					onChange={this.onChange}
					onKeyDown={(e: any) => {
						if (e.key === 'Escape') {
							this.props.onMenuClosed();
							e.stopPropagation();
							this.props.refocusEditLink();
						}
					}}
					onBlur={() => {
						this.props.refocusEditLink();
						this.props.onMenuClosed();
					}}
					onFocus={this.handleFocus}
					menuPosition="fixed"
					captureMenuScroll
					onInputChange={this.handleInputChange}
				/>
			</Suspense>
		);
	}
}

export default AsyncSelectFilterComponent;
